import React, {useRef, useState} from 'react';
import {
  DimensionValue,
  FlatList,
  LayoutChangeEvent,
  ListRenderItem,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';

import config from '../../config';
import {LISTING_CAROUSEL_NUM_ITEMS} from '../../constants/GlobalConstants';
import {space, type} from '../../design-system/tokens';
import {ScreenTypes, useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import theme from '../../style/theme';
import {OrderLine} from '../../transport/apiServiceResponse';
import {visibleScrollbarProps} from '../../utils/webScrollbar';
import {ProductTileCarousel} from '../ProductTile';
import {TILE_DIMENSIONS} from '../ProductTile/styles';
import Text from '../themed/Text';

type PromotionProductListingProps = {
  items: OrderLine[];
  // DIS-1951: brand-detail department rails replace the promotion wording.
  countLabel?: string;
  // DIS-1951: names the section in every accessibility label, so two rails with
  // equal counts on one page stay distinguishable to a screen reader.
  sectionName?: string;
};

// Width of one rail tile plus its Spacer; an arrow press scrolls one viewport less
// this, so the last visible tile stays on screen as an anchor.
const RAIL_TILE_STEP = TILE_DIMENSIONS.width + 8;

/**
 * DIS-1458: Promotion Detail linked-products listing.
 * Collapsed (default) shows a horizontal carousel capped at
 * LISTING_CAROUSEL_NUM_ITEMS tiles with a trailing "See all" card when the
 * promotion has more products. Expanding swaps the carousel for a responsive,
 * non-scrolling inline grid of ALL products (it lives inside the screen's
 * single ScrollView, so it must not nest-scroll).
 * DIS-1951: also renders each department rail on the brand detail page.
 */
const PromotionProductListing = ({
  items,
  countLabel: countLabelProp,
  sectionName,
}: PromotionProductListingProps) => {
  const [expanded, setExpanded] = useState(false);
  // Measured height of a product tile — applied to the "See all" card so it
  // matches the tiles exactly (tiles are a fixed/uniform height, so measuring
  // the first one is enough) and reads as one unified row.
  const [tileHeight, setTileHeight] = useState<number>();
  const {screenType, isWebDesktopScreen} = useWebScreenDimensions();
  // DIS-1951: desktop-web ‹ › arrows. Measurements live in refs so scrolling does
  // not re-render; only the two edge flags are state, and they change rarely.
  const railRef = useRef<FlatList<OrderLine>>(null);
  const offsetRef = useRef(0);
  const viewportWidthRef = useRef(0);
  const contentWidthRef = useRef(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  // DIS-2005: true once the measured rail content is wider than its viewport.
  const [railOverflows, setRailOverflows] = useState(false);

  const cap = LISTING_CAROUSEL_NUM_ITEMS;
  const total = items.length;
  const hasOverflow = total > cap;
  // Desktop web only: mouse users have no horizontal-scroll gesture. Touch and native
  // users swipe the rail instead. DIS-2005: gated on the measured width, not on
  // hasOverflow — a rail of 12 or fewer tiles can still be wider than the viewport.
  const showArrows = isWebDesktopScreen && railOverflows && !expanded;
  const trackOffset = showArrows || Platform.OS === 'web';

  // Promotion has linked barcodes but the current store carries none of them
  // (the parent only mounts this once barcodes exist, so total === 0 means the
  // store has zero matching products). Show a centred, brand-fonted notice in
  // place of the product list rather than an empty section.
  if (total === 0) {
    return (
      <View testID="promotion-product-listing" style={styles.emptyContainer}>
        <Text testID="promotion-products-empty" style={styles.emptyMessage}>
          Not available at this store right now.
        </Text>
      </View>
    );
  }

  let numColumns = 2;
  switch (screenType) {
    case ScreenTypes.desktop:
      numColumns = 4;
      break;
    case ScreenTypes.desktopLarge:
      numColumns = 6;
      break;
  }

  // Fixed fractional width per grid cell so rows wrap cleanly and a lone last-row
  // tile stays column-width. The expanded grid is a flex-wrap View (not a FlatList):
  // it lives inside the screen's vertical ScrollView and is fully in memory, so a
  // plain wrap avoids nested-VirtualizedList warnings. It is not virtualized: a
  // brand-detail department can hold up to 200 lines (DIS-1951 page_size), all of
  // which mount on expand.
  const gridItemWidth: DimensionValue = `${100 / numColumns}%`;

  const maxOffset = () => Math.max(0, contentWidthRef.current - viewportWidthRef.current);

  const syncEdges = () => {
    setAtStart(offsetRef.current <= 0);
    setAtEnd(offsetRef.current >= maxOffset() - 1);
    setRailOverflows(maxOffset() > 1);
  };

  const expand = () => setExpanded(true);
  // The rail remounts at offset 0 when the grid collapses, so the arrows restart there.
  const collapse = () => {
    offsetRef.current = 0;
    syncEdges();
    setExpanded(false);
  };

  const handleRailLayout = (e: LayoutChangeEvent) => {
    viewportWidthRef.current = e.nativeEvent.layout.width;
    syncEdges();
  };

  const handleRailContentSizeChange = (width: number) => {
    contentWidthRef.current = width;
    syncEdges();
  };

  const handleRailScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    offsetRef.current = e.nativeEvent.contentOffset.x;
    syncEdges();
  };

  const scrollRail = (direction: -1 | 1) => {
    const step = Math.max(RAIL_TILE_STEP, viewportWidthRef.current - RAIL_TILE_STEP);
    const offset = Math.min(Math.max(offsetRef.current + direction * step, 0), maxOffset());
    railRef.current?.scrollToOffset({offset, animated: true});
  };

  const productsLabel = sectionName ? `${sectionName} products` : 'products';

  const countLabel =
    countLabelProp ?? `${total} ${total === 1 ? 'PRODUCT' : 'PRODUCTS'} IN THIS PROMOTION`;

  const handleTileLayout = (e: LayoutChangeEvent) => {
    setTileHeight(e.nativeEvent.layout.height);
  };

  const renderCarouselItem: ListRenderItem<OrderLine> = ({item, index}) => (
    <View onLayout={index === 0 ? handleTileLayout : undefined}>
      <ProductTileCarousel item={item} testContext="carousel" />
    </View>
  );

  return (
    <View testID="promotion-product-listing">
      <View style={styles.header}>
        <Text style={styles.countLabel}>{countLabel}</Text>
        <View style={styles.headerActions}>
          {showArrows ? (
            <>
              <Pressable
                testID="promotion-products-scroll-left"
                onPress={() => scrollRail(-1)}
                disabled={atStart}
                accessibilityRole="button"
                accessibilityLabel={`Scroll ${productsLabel} left`}
                style={[styles.arrowButton, atStart && styles.arrowDisabled]}>
                <Text style={styles.arrowGlyph}>{'‹'}</Text>
              </Pressable>
              <Pressable
                testID="promotion-products-scroll-right"
                onPress={() => scrollRail(1)}
                disabled={atEnd}
                accessibilityRole="button"
                accessibilityLabel={`Scroll ${productsLabel} right`}
                style={[styles.arrowButton, atEnd && styles.arrowDisabled]}>
                <Text style={styles.arrowGlyph}>{'›'}</Text>
              </Pressable>
            </>
          ) : null}
          {hasOverflow ? (
            expanded ? (
              <Pressable
                testID="promotion-products-collapse"
                onPress={collapse}
                accessibilityRole="button"
                accessibilityLabel={`Collapse ${productsLabel}`}>
                <Text style={styles.headerLink}>COLLAPSE</Text>
              </Pressable>
            ) : (
              <Pressable
                testID="promotion-products-expand"
                onPress={expand}
                accessibilityRole="button"
                accessibilityLabel={`Expand all ${total} ${productsLabel}`}>
                <Text style={styles.headerLink}>EXPAND ALL</Text>
              </Pressable>
            )
          ) : null}
        </View>
      </View>

      {expanded ? (
        <View testID="promotion-products-grid" style={styles.grid}>
          {/* Renders every linked product with no cap/virtualization, inline inside
              the screen's ScrollView. A promotion's set is Airtable-curated via
              promotion.barcodes; a brand-detail department is not curated and can
              reach 200 lines (DIS-1951). */}
          {items.map(item => (
            <View
              key={String(item.product_id)}
              style={[styles.tileWrapper, {width: gridItemWidth}]}>
              <ProductTileCarousel item={item} fullWidth testContext="grid" />
            </View>
          ))}
        </View>
      ) : (
        <FlatList
          ref={railRef}
          testID="promotion-products-carousel"
          horizontal
          style={styles.carouselList}
          contentContainerStyle={[
            styles.carouselContent,
            isWebDesktopScreen && styles.carouselContentWithScrollbar,
          ]}
          data={items.slice(0, cap)}
          keyExtractor={item => String(item.product_id)}
          renderItem={renderCarouselItem}
          ItemSeparatorComponent={Spacer}
          // DIS-1555: desktop-web mouse users have no horizontal-scroll gesture, so the
          // hidden indicator left the trailing "See all" card unreachable. The helper
          // forces the bar to stay visible — macOS overlay scrollbars auto-hide when
          // idle, so the flag alone still leaves no visible affordance.
          showsHorizontalScrollIndicator={isWebDesktopScreen}
          {...visibleScrollbarProps(isWebDesktopScreen)}
          onLayout={handleRailLayout}
          onContentSizeChange={handleRailContentSizeChange}
          // Web keeps the offset current even while the arrows are hidden (narrow window),
          // so they start correct after a resize to desktop. Native has no arrows and
          // gets no per-frame callbacks.
          onScroll={trackOffset ? handleRailScroll : undefined}
          scrollEventThrottle={trackOffset ? 16 : undefined}
          ListFooterComponent={
            hasOverflow ? (
              <Pressable
                testID="promotion-products-see-all"
                onPress={expand}
                accessibilityRole="button"
                accessibilityLabel={`See all ${total} ${productsLabel}`}
                style={[styles.seeAllCard, tileHeight != null && {height: tileHeight}]}>
                <Text style={styles.seeAllCount}>{`+${total - cap}`}</Text>
                <Text style={styles.seeAllLabel}>{`SEE ALL ${total} PRODUCTS`}</Text>
                <Text style={styles.seeAllChevron}>{'›'}</Text>
              </Pressable>
            ) : null
          }
        />
      )}
    </View>
  );
};

function Spacer() {
  return <View style={styles.spacer} />;
}

// Section-accent scheme (header count, 2px border, expand/collapse links), keyed by
// tenant so the colour choice can change without renaming anything:
// Thirsty Camel + Harry Brown use their brand black (text.heading.primary);
// Bottlemart + Sip'n'Save use the app-wide link blue.
const ACCENT =
  config.BASE_TENANT === 'TC' || config.BASE_TENANT === 'HB'
    ? theme.colour.text.heading.primary
    : theme.colour.text.link.primary;
// "See all" card is a CTA, so it uses the brand button token pair (matches Add-to-cart).
const CTA_BG = theme.colour.background.button.primary;
const CTA_ON = theme.colour.background.button.contrastOnPrimary;

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 32,
    paddingBottom: 12,
    borderBottomWidth: 2,
    borderBottomColor: ACCENT,
  },
  countLabel: {
    flexShrink: 1,
    marginRight: 12,
    color: ACCENT,
    fontSize: 14,
    fontWeight: 'bold',
    letterSpacing: 0.8,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.s12,
  },
  headerLink: {
    color: ACCENT,
    fontSize: 12,
    fontWeight: 'bold',
    letterSpacing: 0.5,
    textDecorationLine: 'underline',
  },
  // Round outlined ‹ › buttons in the section accent; 32px keeps a usable mouse target.
  // SHORTCUT: 32/16/1.5 sizes and lineHeight 22 are literals — no control-size token covers an icon button
  // yet; move to the control tokens when DIS-1735 adds one.
  arrowButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: ACCENT,
    alignItems: 'center',
    justifyContent: 'center',
  },
  arrowGlyph: {
    ...type.heading.h1,
    color: ACCENT,
    lineHeight: 22,
  },
  arrowDisabled: {
    opacity: 0.3,
  },
  carouselList: {
    marginTop: 16,
  },
  carouselContent: {
    paddingBottom: 6,
  },
  // The always-visible desktop-web scrollbar sits inside the scroll box, so the
  // content needs to clear its 10px track instead of being overlapped by it.
  carouselContentWithScrollbar: {
    paddingBottom: 16,
  },
  spacer: {
    width: 8,
  },
  seeAllCard: {
    width: TILE_DIMENSIONS.width,
    marginLeft: 8,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 10,
    backgroundColor: CTA_BG,
  },
  seeAllCount: {
    color: CTA_ON,
    fontSize: 32,
    fontWeight: 'bold',
  },
  seeAllLabel: {
    marginTop: 8,
    color: CTA_ON,
    fontSize: 12,
    fontWeight: 'bold',
    letterSpacing: 0.5,
    textAlign: 'center',
  },
  seeAllChevron: {
    marginTop: 2,
    color: CTA_ON,
    fontSize: 22,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 16,
    paddingHorizontal: 4,
  },
  tileWrapper: {
    paddingHorizontal: 4,
    marginBottom: 8,
  },
  emptyContainer: {
    marginTop: 16,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyMessage: {
    color: theme.colour.text.content.primary,
    fontSize: 15,
    textAlign: 'center',
  },
});

export default PromotionProductListing;
